import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPublishedArticles } from "@/lib/cms/journal.functions";
import {
  ARTICLES_FALLBACK,
  JOURNAL_PLACEHOLDER,
  deriveAlt,
  formatArticleDate,
  imageForSlug,
  isValidHttpsUrl,
  type ArticleItem,
} from "@/lib/journal-defaults";

export type UseJournalArticlesResult = {
  articles: ArticleItem[];
  isFallback: boolean;
  isLoading: boolean;
};

export function useJournalArticles(): UseJournalArticlesResult {
  const fetchFn = useServerFn(listPublishedArticles);
  const { data, isLoading } = useQuery({
    queryKey: ["journal", "public"],
    queryFn: () => fetchFn(),
    staleTime: 60_000,
  });

  if (data === null || data === undefined) {
    if (isLoading) {
      return { articles: [], isFallback: false, isLoading: true };
    }
    return { articles: ARTICLES_FALLBACK, isFallback: true, isLoading: false };
  }

  const articles: ArticleItem[] = data.map((r) => {
    const image = isValidHttpsUrl(r.imageUrl)
      ? r.imageUrl
      : imageForSlug(r.slug) ?? JOURNAL_PLACEHOLDER;
    return {
      id: r.id,
      slug: r.slug,
      category: r.category,
      title: r.title,
      excerpt: r.excerpt,
      date: formatArticleDate(r.publishedAt),
      readTime: r.readTime,
      author: r.author,
      image,
      alt: deriveAlt(r.altText, r.title),
      body: r.body,
      isDemo: r.isDemo,
      publishedAt: r.publishedAt,
    };
  });

  return { articles, isFallback: false, isLoading: false };
}